import React, { useState, useEffect } from 'react';
import { Listing, Order, PayoutRequest, LedgerEntry, Game } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { 
  DollarSign, Package, PlusCircle, Upload, ArrowDownToLine, 
  Clock, ShieldCheck, CheckCircle2, AlertCircle, FileText, PauseCircle, PlayCircle 
} from 'lucide-react';
import { VerifiedSellerBadge } from '../../components/common/Badge';

export const SellerDashboard: React.FC = () => {
  const { user } = useAuth();

  const [tab, setTab] = useState<'listings' | 'orders' | 'payouts' | 'bulk_import'>('listings');
  const [listings, setListings] = useState<Listing[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [payouts, setPayouts] = useState<PayoutRequest[]>([]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [balance, setBalance] = useState({ available_balance_cents: 0, pending_balance_cents: 0 });
  const [games, setGames] = useState<Game[]>([]);

  // Create Listing Modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newGameId, setNewGameId] = useState('gm_val');
  const [newCategory, setNewCategory] = useState('accounts');
  const [newPrice, setNewPrice] = useState('49.99');
  const [newDescription, setNewDescription] = useState('');
  const [newDeliveryType, setNewDeliveryType] = useState('automatic');
  const [newDigitalKeys, setNewDigitalKeys] = useState('');

  // Bulk Import state
  const [importJson, setImportJson] = useState(`[
  {
    "game_id": "gm_val",
    "category_slug": "accounts",
    "title": "Valorant Diamond 1 Smurf Account [Full Access]",
    "description": "Clean ranked account with verified unrated MMR.",
    "price_cents": 4500,
    "stock_count": 1,
    "delivery_type": "automatic"
  }
]`);
  const [importReport, setImportReport] = useState<any>(null);

  // Payout request modal
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('50.00');
  const [payoutMethod, setPayoutMethod] = useState<'crypto_usdt' | 'bank_wire' | 'paypal_sandbox'>('crypto_usdt');
  const [payoutDestination, setPayoutDestination] = useState('0x71C...8942 (USDT TRC20)');
  const [payoutStatusMsg, setPayoutStatusMsg] = useState('');

  // Manual delivery modal
  const [deliverModalOpen, setDeliverModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [deliveryInstructions, setDeliveryInstructions] = useState('');

  const fetchDashboardData = async () => {
    try {
      const [lRes, oRes, pRes, gRes] = await Promise.all([
        fetch('/api/seller/listings'),
        fetch('/api/orders?as=seller'),
        fetch('/api/seller/payouts'),
        fetch('/api/games')
      ]);

      if (lRes.ok) {
        const lData = await lRes.json();
        if (lData.success) setListings(lData.listings || []);
      }
      if (oRes.ok) {
        const oData = await oRes.json();
        if (oData.success) setOrders(oData.orders || []);
      }
      if (pRes.ok) {
        const pData = await pRes.json();
        if (pData.success) {
          setPayouts(pData.payouts || []);
          setLedger(pData.ledger || []);
          if (pData.balance) setBalance(pData.balance);
        }
      }
      if (gRes.ok) {
        const gData = await gRes.json();
        if (gData.success) setGames(gData.games || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleCreateListing = async (e: React.FormEvent) => {
    e.preventDefault();
    const priceCents = Math.round(parseFloat(newPrice) * 100);
    const keysArray = newDigitalKeys.split('\n').map(s => s.trim()).filter(Boolean);

    try {
      const res = await fetch('/api/listings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_id: newGameId,
          category_slug: newCategory,
          title: newTitle,
          description: newDescription,
          price_cents: priceCents,
          delivery_type: newDeliveryType,
          keys_to_stock: keysArray
        })
      });

      if (res.ok) {
        setCreateModalOpen(false);
        setNewTitle('');
        setNewDescription('');
        setNewDigitalKeys('');
        await fetchDashboardData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleBulkImport = async () => {
    try {
      const parsed = JSON.parse(importJson);
      const res = await fetch('/api/listings/bulk-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: parsed })
      });
      const data = await res.json();
      if (data.success) {
        setImportReport(data.report);
        await fetchDashboardData();
      }
    } catch (err: any) {
      setImportReport({ failed: 1, errors: ['Invalid JSON format: ' + err.message] });
    }
  };

  const handleRequestPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(payoutAmount) * 100);
    setPayoutStatusMsg('');

    try {
      const res = await fetch('/api/seller/payouts/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: amountCents,
          method: payoutMethod,
          destination_info: payoutDestination
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPayoutStatusMsg('Payout requested successfully! Staff will process disbursement.');
        setPayoutModalOpen(false);
        await fetchDashboardData();
      } else {
        setPayoutStatusMsg(data.error || 'Failed to request payout');
      }
    } catch (err: any) {
      setPayoutStatusMsg(err.message || 'Error requesting payout');
    }
  };

  const handleMarkDelivered = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrderId) return;

    try {
      const res = await fetch(`/api/orders/${selectedOrderId}/deliver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instructions: deliveryInstructions })
      });
      if (res.ok) {
        setDeliverModalOpen(false);
        setSelectedOrderId(null);
        setDeliveryInstructions('');
        await fetchDashboardData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      
      {/* Top Banner Stats */}
      <div className="p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl sm:text-3xl font-black text-brand-lightText dark:text-brand-darkText">
              Seller Merchant Center
            </h1>
            <VerifiedSellerBadge size="md" />
          </div>
          <p className="text-xs text-brand-lightSecText dark:text-brand-darkSecText mt-1">
            Store: <strong>{user?.sellerProfile?.store_name || user?.username}</strong> • Verified Level: Tier 1
          </p>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <span className="text-[11px] font-bold uppercase tracking-wider text-brand-lightSecText block">Available Balance</span>
            <span className="font-black text-2xl text-emerald-600 dark:text-emerald-400">
              ${(balance.available_balance_cents / 100).toFixed(2)} USD
            </span>
            <span className="text-[10px] text-brand-lightSecText block">Pending Escrow: ${(balance.pending_balance_cents / 100).toFixed(2)}</span>
          </div>

          <button
            onClick={() => setPayoutModalOpen(true)}
            className="px-5 py-3 rounded-2xl font-bold text-xs text-white bg-emerald-600 hover:bg-emerald-700 shadow-lg flex items-center gap-1.5 transition-all"
          >
            <ArrowDownToLine className="w-4 h-4" />
            <span>Request Payout</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-brand-lightBorder dark:border-brand-darkBorder pb-4">
        {[
          { id: 'listings', label: `My Listings (${listings.length})`, icon: Package },
          { id: 'orders', label: `Incoming Orders (${orders.length})`, icon: Clock },
          { id: 'payouts', label: `Payouts & Ledger (${payouts.length})`, icon: DollarSign },
          { id: 'bulk_import', label: 'Bulk CSV / JSON Import', icon: Upload },
        ].map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id as any)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold transition-all ${
                isActive
                  ? 'bg-brand-blue text-white shadow-glow-blue'
                  : 'bg-brand-lightSec dark:bg-brand-darkCard text-brand-lightSecText hover:text-brand-lightText'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab 1: Listings */}
      {tab === 'listings' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-lg">Active Store Listings</h2>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="px-4 py-2 rounded-xl font-bold text-xs text-white bg-brand-blue hover:bg-brand-hover shadow-glow-blue flex items-center gap-1.5 transition-all"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Create New Listing</span>
            </button>
          </div>

          <div className="space-y-3">
            {listings.map((l) => (
              <div
                key={l.id}
                className="p-5 rounded-2xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder flex items-center justify-between gap-4"
              >
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-brand-blue block">
                    {l.game_name} • {l.category_slug} • {l.delivery_type}
                  </span>
                  <h3 className="font-bold text-sm truncate mt-0.5">{l.title}</h3>
                  <p className="text-xs text-brand-lightSecText">
                    Stock: <strong>{l.stock_count}</strong> • Warranty: <strong>{l.warranty_hours}h</strong> • Status: <strong className="text-emerald-500 uppercase">{l.status}</strong>
                  </p>
                </div>

                <div className="text-right flex-shrink-0">
                  <span className="font-black text-base text-brand-blue dark:text-brand-accent block">
                    ${(l.price_cents / 100).toFixed(2)} USD
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 2: Incoming Orders */}
      {tab === 'orders' && (
        <div className="space-y-4">
          <h2 className="font-bold text-lg">Orders to Fulfill</h2>

          {orders.map((o) => (
            <div
              key={o.id}
              className="p-6 rounded-2xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold">#{o.order_number}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-500/10 text-brand-blue">
                    {o.status}
                  </span>
                </div>
                <h3 className="font-bold text-sm mt-1">{o.listing_title}</h3>
                <p className="text-xs text-brand-lightSecText mt-0.5">
                  Buyer: <strong>{o.buyer_username}</strong> • Net Earning: <strong className="text-emerald-500">${(o.seller_net_cents / 100).toFixed(2)}</strong> (Fee: ${(o.fee_cents / 100).toFixed(2)})
                </p>
                {o.buyer_notes && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Buyer Note: {o.buyer_notes}
                  </p>
                )}
              </div>

              {o.status === 'paid' && o.delivery_type !== 'automatic' && (
                <button
                  onClick={() => { setSelectedOrderId(o.id); setDeliverModalOpen(true); }}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-brand-blue"
                >
                  Submit Manual Delivery
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tab 3: Payouts & Ledger */}
      {tab === 'payouts' && (
        <div className="space-y-8">
          <div>
            <h2 className="font-bold text-lg mb-4">Payout Withdrawal History</h2>
            <div className="space-y-3">
              {payouts.map((p) => (
                <div
                  key={p.id}
                  className="p-4 rounded-2xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder flex items-center justify-between text-xs"
                >
                  <div>
                    <span className="font-bold text-sm block">
                      ${(p.amount_cents / 100).toFixed(2)} USD via {p.method}
                    </span>
                    <span className="text-brand-lightSecText block mt-0.5">
                      Destination: {p.destination_info} • Ref: {p.payment_reference || 'Pending'}
                    </span>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full font-bold uppercase text-[10px] ${
                    p.status === 'paid' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'
                  }`}>
                    {p.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-bold text-lg mb-4">Financial Ledger & Audit Entries</h2>
            <div className="divide-y divide-brand-lightBorder dark:divide-brand-darkBorder bg-white dark:bg-brand-darkCard rounded-2xl border p-4 text-xs">
              {ledger.map((l) => (
                <div key={l.id} className="py-2.5 flex items-center justify-between">
                  <div>
                    <span className="font-bold">{l.description}</span>
                    <span className="text-brand-lightSecText block text-[11px]">{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                  <span className={`font-black ${l.amount_cents >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {l.amount_cents >= 0 ? '+' : ''}${(l.amount_cents / 100).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: Bulk CSV / JSON Import */}
      {tab === 'bulk_import' && (
        <div className="p-8 rounded-3xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder space-y-6">
          <div>
            <h2 className="font-bold text-lg">Bulk Inventory Import</h2>
            <p className="text-xs text-brand-lightSecText mt-1">
              Upload multiple listings simultaneously via validated JSON. Includes automatic verification.
            </p>
          </div>

          <textarea
            rows={8}
            value={importJson}
            onChange={(e) => setImportJson(e.target.value)}
            className="w-full p-4 font-mono text-xs rounded-2xl bg-brand-lightSec dark:bg-brand-darkElevated border border-brand-lightBorder dark:border-brand-darkBorder text-brand-lightText dark:text-brand-darkText"
          />

          <button
            onClick={handleBulkImport}
            className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-brand-blue"
          >
            Validate & Import Listings
          </button>

          {importReport && (
            <div className="p-4 rounded-2xl bg-brand-lightSec dark:bg-brand-darkElevated border text-xs space-y-1">
              <p className="font-bold">Import Results:</p>
              <p className="text-emerald-500">Successfully Imported: {importReport.imported || 0}</p>
              {importReport.failed > 0 && (
                <div className="text-rose-500">
                  <p>Failed: {importReport.failed}</p>
                  <ul className="list-disc pl-5">
                    {importReport.errors?.map((err: string, i: number) => <li key={i}>{err}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create Listing Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder rounded-3xl max-w-lg w-full p-6 space-y-4">
            <h3 className="font-bold text-lg">Create New Marketplace Listing</h3>
            <form onSubmit={handleCreateListing} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold mb-1">Game</label>
                <select
                  value={newGameId}
                  onChange={(e) => setNewGameId(e.target.value)}
                  className="w-full p-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                >
                  {games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-1">Category</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full p-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                >
                  <option value="accounts">Game Accounts</option>
                  <option value="items">In-Game Items</option>
                  <option value="currency">Gold & Currency</option>
                  <option value="keys">Activation Keys</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-1">Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Valorant Immortal 3 Clean Smurf"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full p-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold mb-1">Price (USD $)</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    className="w-full p-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1">Delivery Type</label>
                  <select
                    value={newDeliveryType}
                    onChange={(e) => setNewDeliveryType(e.target.value)}
                    className="w-full p-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                  >
                    <option value="automatic">Automatic (Stock Keys)</option>
                    <option value="manual">Manual Transfer</option>
                  </select>
                </div>
              </div>

              {newDeliveryType === 'automatic' && (
                <div>
                  <label className="block font-semibold mb-1">Encrypted Inventory Keys (1 per line)</label>
                  <textarea
                    rows={3}
                    placeholder="user1:pass1&#10;user2:pass2"
                    value={newDigitalKeys}
                    onChange={(e) => setNewDigitalKeys(e.target.value)}
                    className="w-full p-2.5 font-mono rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                  />
                </div>
              )}

              <div>
                <label className="block font-semibold mb-1">Description</label>
                <textarea
                  rows={3}
                  required
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  className="w-full p-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setCreateModalOpen(false)} className="px-4 py-2 font-bold text-slate-500">Cancel</button>
                <button type="submit" className="px-5 py-2 font-bold text-white bg-brand-blue rounded-xl">Publish Listing</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payout Request Modal */}
      {payoutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder rounded-3xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-lg">Request Balance Payout</h3>
            <form onSubmit={handleRequestPayout} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold mb-1">Payout Method</label>
                <select
                  value={payoutMethod}
                  onChange={(e) => setPayoutMethod(e.target.value as any)}
                  className="w-full p-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                >
                  <option value="crypto_usdt">USDT (TRC20 / ERC20)</option>
                  <option value="bank_wire">International Bank Wire</option>
                  <option value="paypal_sandbox">PayPal Sandbox</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold mb-1">Amount (USD $)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  className="w-full p-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                />
              </div>

              <div>
                <label className="block font-semibold mb-1">Destination Address / IBAN / Account</label>
                <input
                  type="text"
                  required
                  value={payoutDestination}
                  onChange={(e) => setPayoutDestination(e.target.value)}
                  className="w-full p-2.5 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setPayoutModalOpen(false)} className="px-4 py-2 font-bold text-slate-500">Cancel</button>
                <button type="submit" className="px-5 py-2 font-bold text-white bg-emerald-600 rounded-xl">Submit Request</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Delivery Modal */}
      {deliverModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder rounded-3xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-lg">Submit Manual Delivery Evidence</h3>
            <form onSubmit={handleMarkDelivered} className="space-y-4 text-xs">
              <div>
                <label className="block font-semibold mb-1">Instructions / Access Details Sent</label>
                <textarea
                  rows={4}
                  required
                  placeholder="Detail in-game trade confirmation, character transfer notes, or login details..."
                  value={deliveryInstructions}
                  onChange={(e) => setDeliveryInstructions(e.target.value)}
                  className="w-full p-3 rounded-xl bg-brand-lightSec dark:bg-brand-darkElevated border"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setDeliverModalOpen(false)} className="px-4 py-2 font-bold text-slate-500">Cancel</button>
                <button type="submit" className="px-5 py-2 font-bold text-white bg-brand-blue rounded-xl">Confirm Delivery</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
